import { debounce } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { LinkStyle } from 'src/constants/LinkStyle';
import { ReduxStore } from 'src/ducks';
import { resyncUserEmail } from 'src/ducks/user';
import { userEmailWasUpdatedSelector } from 'src/selectors/user';
import { Color, ThemeColors } from 'src/theme/primaryTheme';
import { User } from 'src/types';
import styled from 'styled-components';
import ExternalLink from '../Link/ExternalLink';
import LoadingSpinner from '../LoadingSpinner';
import Text, { TextStyle } from '../Text';
const { REACT_APP_CONTACT_PREFERENCES_CENTER } = process.env;

const UserInfoTable: React.FunctionComponent<{ user: User; emailWasUpdated: boolean }> = ({ user, emailWasUpdated }) => {
  const { isAuthenticated } = useSelector((state: ReduxStore) => state.auth);
  const [retrySyncTimeoutId, setRetrySyncTimeoutId] = useState<NodeJS.Timeout>();
  const [clearSuccessTimeoutId, setClearSuccessTimeoutId] = useState<NodeJS.Timeout>();
  const [syncAttemptNumber, setSyncAttemptNumber] = useState<number>(1);
  const [advantageSyncSuccess, setAdvantageSyncSuccess] = useState<string>();
  const [retriesFailed, setRetriesFailed] = useState<boolean>(false);
  const [clickedUpdateEmail, setClickedUpdateEmail] = useState<boolean>(false);
  const [syncingWithAdvantage, setSyncingWithAdvantage] = useState<boolean>(false);

  const { error } = useSelector((state: ReduxStore) => state.user);

  const dispatch = useDispatch();

  const resyncUserWithAdvantage = useMemo(
    () => async () => {
      if (!syncingWithAdvantage) {
        setSyncingWithAdvantage(true);
        setRetriesFailed(false);
        setAdvantageSyncSuccess(undefined);
      }
      dispatch(resyncUserEmail(user));
    },
    [syncingWithAdvantage, setSyncingWithAdvantage, user, dispatch]
  );

  const handleFocus = useMemo(
    () =>
      debounce(async () => {
        if (isAuthenticated && clickedUpdateEmail && !syncingWithAdvantage) {
          resyncUserWithAdvantage();
        }
      }, 1200),
    [syncingWithAdvantage, clickedUpdateEmail, isAuthenticated, resyncUserWithAdvantage]
  );

  useEffect(() => {
    if (syncingWithAdvantage) {
      if (emailWasUpdated) {
        // If the user was successfully synced, we do not need to run this again
        setClickedUpdateEmail(false);
        setSyncingWithAdvantage(false);
        setAdvantageSyncSuccess('Email update was successful.');
        const timeoutId = setTimeout(() => setAdvantageSyncSuccess(undefined), 30 * 1000);
        setClearSuccessTimeoutId(timeoutId);
      } else {
        if (syncAttemptNumber === 3) {
          setRetriesFailed(true);
          Rollbar.error(`Syncing Advantage user with EHR User ID ${user.id} returned the un-updated email three times in a row.`);
          setSyncingWithAdvantage(false);
          setClickedUpdateEmail(false);
          setSyncAttemptNumber(1); // Reset the counter in case the user wants to try again
        } else {
          setSyncAttemptNumber((oldAttemptNumber) => oldAttemptNumber + 1);
          const timeoutId = setTimeout(() => resyncUserWithAdvantage(), 30 * 1000);
          setRetrySyncTimeoutId(timeoutId);
        }
      }
    }
  }, [user.emails]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window.addEventListener('focus', handleFocus);
    return () => {
      if (retrySyncTimeoutId) {
        clearTimeout(retrySyncTimeoutId);
      }
      if (clearSuccessTimeoutId) {
        clearTimeout(clearSuccessTimeoutId);
      }
      window.removeEventListener('focus', handleFocus);
    };
  }, [handleFocus, retrySyncTimeoutId, clearSuccessTimeoutId]);

  const handleContactPreferencesCenterClick = () => {
    setClickedUpdateEmail(true);
  };

  const syncMessageColor = useMemo(() => {
    if (retriesFailed || error?.emailSyncError || error?.message) {
      return Color.Error;
    } else if (advantageSyncSuccess) {
      return Color.ConfirmationDark;
    }
    return Color.Label;
  }, [retriesFailed, error?.emailSyncError, error?.message, advantageSyncSuccess]);

  const isChecking = syncingWithAdvantage && !retriesFailed && !error?.emailSyncError && !error?.message && !advantageSyncSuccess;

  const supportLink = (
    <ExternalLink target="_blank" rel="noopener noreferrer" linkStyle={LinkStyle.ExternalLink} href="https://support.edhub.com/hc/en-us">
      contact support
    </ExternalLink>
  );

  const renderSyncMessage = () => {
    if (retriesFailed) {
      return (
        <>
          The current email matches an email already on file. To update the email address{' '}
          <ExternalLink
            target="_blank"
            rel="noopener noreferrer"
            linkStyle={LinkStyle.ExternalLink}
            href={REACT_APP_CONTACT_PREFERENCES_CENTER}
          >
            contact AMC
          </ExternalLink>
        </>
      );
    }
    if (error?.emailSyncError === 'noEmail') {
      return (
        <>
          Please{' '}
          <ExternalLink
            target="_blank"
            rel="noopener noreferrer"
            linkStyle={LinkStyle.ExternalLink}
            href={REACT_APP_CONTACT_PREFERENCES_CENTER}
          >
            add an email
          </ExternalLink>{' '}
          to proceed or {supportLink} if you have questions.
        </>
      );
    }
    if (error?.emailSyncError === 'otherAccount') {
      return <>This email is associated with another account, a support ticket has been opened. Please {supportLink} to resolve.</>;
    }
    if (error?.emailSyncError === 'generic') {
      return <>Something went wrong. Please try again or {supportLink} to resolve.</>;
    }
    if (error?.message) {
      return error.message;
    }
    if (advantageSyncSuccess) {
      return advantageSyncSuccess;
    }
    return 'Checking for updated email, this may take a minute or two...';
  };

  return (
    <StyledTable data-testid="user-info">
      <tbody>
        <tr>
          <td>
            <Text textStyle={TextStyle.SubTitle} bold={true} color={Color.Primary}>
              First Name
            </Text>
          </td>
          <td>
            <Text textStyle={TextStyle.SubTitle} color={Color.Primary}>
              {user.firstName}
            </Text>
          </td>
        </tr>
        <tr>
          <td>
            <Text textStyle={TextStyle.SubTitle} bold={true} color={Color.Primary}>
              Last Name
            </Text>
          </td>
          <td>
            <Text textStyle={TextStyle.SubTitle} color={Color.Primary}>
              {user.lastName}
            </Text>
          </td>
        </tr>
        <tr>
          <td>
            <Text textStyle={TextStyle.SubTitle} bold={true} color={Color.Primary}>
              Email Address
            </Text>
          </td>
          <td>
            <Text textStyle={TextStyle.SubTitle} color={Color.Primary}>
              {user.emailAddress || (user.emails && user.emails[0])}
            </Text>
          </td>
          <td>
            <ExternalLink
              linkStyle={LinkStyle.ExternalLink}
              href={REACT_APP_CONTACT_PREFERENCES_CENTER}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleContactPreferencesCenterClick}
              title="Update email address"
            >
              Update email address
            </ExternalLink>
          </td>
        </tr>
        <tr>
          <td>
            <Text textStyle={TextStyle.SubTitle} bold={true} color={Color.Primary}>
              AMA Membership
            </Text>
          </td>
          <td>
            <Text data-test-id="membershipStatus" textStyle={TextStyle.SubTitle} color={Color.Primary}>
              {user.amaMember ? 'AMA Member' : 'Non-member'}
            </Text>
          </td>
          {!user.amaMember && (
            <td>
              <ExternalLink linkStyle={LinkStyle.ExternalLink} href="https://www.ama-assn.org/amaone/membership" title="Join the AMA">
                Join the AMA
              </ExternalLink>
            </td>
          )}
        </tr>
        {(syncingWithAdvantage || retriesFailed || error?.emailSyncError || error?.message || advantageSyncSuccess) && (
          <tr>
            <td colSpan={3}>
              <SyncMessage role={retriesFailed || error?.emailSyncError || error?.message ? 'alert' : 'status'}>
                {isChecking && (
                  <SpinnerContainer>
                    <LoadingSpinner size={16} fill={ThemeColors.label} />
                  </SpinnerContainer>
                )}
                <Text textStyle={TextStyle.SubTitle} bold={true} color={syncMessageColor}>
                  {renderSyncMessage()}
                </Text>
              </SyncMessage>
            </td>
          </tr>
        )}
      </tbody>
    </StyledTable>
  );
};

const SyncMessage = styled.div`
  display: flex;
  align-items: center;
`;

// LoadingSpinner centers its svg in a full-width div, so override that to keep it inline.
const SpinnerContainer = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  margin-right: 0.8rem;

  & > div {
    width: auto;
  }
`;

const StyledTable = styled.table`
  width: 80rem;
  border-spacing: 0;
  border-collapse: collapse;
  margin-bottom: 4.8rem;

  & td {
    padding: 0.5rem 0;
  }
`;

const mapStateToProps = (state: ReduxStore) => {
  return {
    emailWasUpdated: userEmailWasUpdatedSelector(state),
  };
};

export default connect(mapStateToProps)(UserInfoTable);
